import axios from "axios";
import { Message } from "element-ui";
import store from "@/store";
import { getToken } from "@/utils/auth";
import router from "@/router";
import { getApiBase } from "@/utils/apiBase";
import { isAdminDomain } from "@/utils/siteMode";
import { openPortalLogin } from "@/utils/portalLogin";

const service = axios.create({
  baseURL: getApiBase(),
  timeout: 60000,
});

function safeGotoLogin(hadToken) {
  const route = router.currentRoute || {};
  const p = route.path || "";
  const onProtected =
    route.matched && route.matched.some((m) => m.meta && m.meta.needAuth);
  if (p.startsWith("/admin") || isAdminDomain()) {
    if (!onProtected) return;
    if (p === "/admin/login") return;
    router.replace("/admin/login").catch(function () {});
    return;
  }
  if (onProtected || !hadToken) {
    openPortalLogin(route.fullPath || route.path);
  }
}

/** 业务错误 reject；403 标记 silent，供全局吞掉未 catch 的权限拒绝 */
function rejectBiz(msg, code) {
  const err = new Error(msg || "error");
  if (code === 403) err.silent = true;
  return Promise.reject(err);
}

service.interceptors.request.use(
  (config) => {
    const t = getToken();
    if (t) {
      config.headers["Admin-Token"] = t;
    }
    return config;
  },
  (err) => Promise.reject(err)
);

service.interceptors.response.use(
  (res) => {
    const r = res.data;
    if (r.code === 200) {
      // 登录接口 token 在顶层
      return r;
    }
    if (r.code === 401) {
      const hadToken = !!getToken();
      store.commit("LOGOUT");
      safeGotoLogin(hadToken);
      // 游客页允许未登录浏览：401 不弹红框刷屏
      return rejectBiz(r.msg || "未授权，请先登录", r.code);
    }
    // 403：多为角色与接口不匹配，避免多接口并发时满屏 Message；页面可自行 catch 提示
    if (r.code !== 403) {
      Message.error(r.msg || "请求失败");
    }
    return rejectBiz(r.msg || "error", r.code);
  },
  (err) => {
    let msg = err.message || "网络错误";
    const reqUrl = (err.config && err.config.url) || "";
    const isUpload = reqUrl.indexOf("/common/upload") >= 0;
    const st = err.response && err.response.status;
    if (!err.response && isUpload) {
      msg =
        "上传连接中断且无返回数据：多为管理端或 API 站 Nginx 超时(需 600s)、Gunicorn --timeout 600、或服务器磁盘/tmp 已满。请到宝塔查看 cloudapi/cloudfront 错误日志与后端 upload 日志";
    } else if (!err.response && /network/i.test(msg)) {
      msg = "网络异常，请检查 API 地址协议(HTTPS)是否与页面一致";
    }
    if (err.code === "ECONNABORTED" || /timeout/i.test(msg)) {
      if (isUpload) {
        msg =
          "上传超时(10分钟)：文件可能已传到服务器但转存 COS 未完成，请查后端日志 upload cos；并确认 Nginx client_max_body_size 850m 与 proxy_read_timeout 600s";
      } else {
        msg = "请求超时，请稍后重试或减少题量";
      }
    }
    if (st === 413) {
      msg = "文件超过 Nginx 限制(413)：请在 cloudapi 与 cloudfront 站点配置 client_max_body_size 850m 并重载 Nginx";
    } else if (st === 502 || st === 504) {
      msg =
        "网关错误(" +
        st +
        ")：后端处理中断，请确认 Gunicorn --timeout 600 已重启，且 cloudfront 的 /prod-api/ 也有 600s 超时";
    }
    Message.error(msg);
    const wrapped = new Error(msg);
    wrapped.silent = true;
    return Promise.reject(wrapped);
  }
);

export default service;
